import React from 'react';
import {
  ActivityIndicator,
  Platform,
  Pressable,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';

import ScreenDimensions from '../../../../constants/ScreenDimensionsConfig';
import theme from '../../../../style/theme';
import Text from '../../../themed/Text';

import {ApplePayIcon, CardBrandIcon, GooglePayIcon, SupportedCardIcons} from './PaymentIcons';
import type {PaymentMethodType, SavedCard} from './types';

type PaymentMethodOptionProps = {
  type: PaymentMethodType;
  isSelected: boolean;
  onSelect: () => void;
  savedCard?: SavedCard;
  supportedCardBrands?: string[];
  disabled?: boolean;
  comingSoon?: boolean;
  /** Message explaining why this payment method is unavailable */
  unavailableReason?: string;
  /** Card action callbacks - only used when savedCard is provided */
  onChangeCard?: () => void;
  onDeleteCard?: () => void;
  isCardActionLoading?: boolean;
  children?: React.ReactNode;
};

function RadioButton({selected, disabled}: {selected: boolean; disabled?: boolean}) {
  return (
    <View style={[styles.radio, disabled && styles.radioDisabled]}>
      {selected ? (
        <View style={[styles.radioInner, disabled && styles.radioInnerDisabled]} />
      ) : null}
    </View>
  );
}

function formatExpiry(month: number, year: number): string {
  const mm = month.toString().padStart(2, '0');
  const yy = year.toString().slice(-2);
  return `${mm}/${yy}`;
}

function getMethodLabel(type: PaymentMethodType, savedCard?: SavedCard): string {
  switch (type) {
    case 'card':
      if (savedCard) {
        return `${savedCard.cardType.toUpperCase()} ****${savedCard.lastFourDigits}`;
      }
      return 'Credit/Debit Card';
    case 'applePay':
      return 'Apple Pay';
    case 'googlePay':
      return 'Google Pay';
  }
}

function getMethodIcon(
  type: PaymentMethodType,
  savedCard?: SavedCard,
  supportedCardBrands?: string[],
) {
  switch (type) {
    case 'card':
      if (savedCard) {
        return <CardBrandIcon cardType={savedCard.cardType} />;
      }
      return <SupportedCardIcons supportedCardBrands={supportedCardBrands} />;
    case 'applePay':
      return <ApplePayIcon />;
    case 'googlePay':
      return <GooglePayIcon />;
  }
}

function ComingSoonBadge() {
  return (
    <View style={styles.comingSoonBadge}>
      <Text style={styles.comingSoonText}>Coming soon</Text>
    </View>
  );
}

function UnavailableBadge() {
  return (
    <View style={styles.unavailableBadge}>
      <Text style={styles.unavailableBadgeText}>Unavailable</Text>
    </View>
  );
}

export default function PaymentMethodOption({
  type,
  isSelected,
  onSelect,
  savedCard,
  supportedCardBrands,
  disabled = false,
  comingSoon = false,
  unavailableReason,
  onChangeCard,
  onDeleteCard,
  isCardActionLoading = false,
  children,
}: PaymentMethodOptionProps) {
  const {width: viewportWidth} = useWindowDimensions();
  const label = getMethodLabel(type, savedCard);
  const icon = getMethodIcon(type, savedCard, supportedCardBrands);
  const isUnavailable = !!unavailableReason;
  const isDisabled = disabled || comingSoon || isUnavailable;
  const isWebDesktop =
    Platform.OS === 'web' && viewportWidth > ScreenDimensions.mobileOrTablet.maxWidth;

  // For card type with saved card, render inline card details
  const showInlineCardDetails = type === 'card' && savedCard && !comingSoon && !isUnavailable;
  // For card type without saved card, show "Add →" action
  const showAddCardAction = type === 'card' && !savedCard && !comingSoon && !isUnavailable;

  // Don't show selected state for "Add card" option (no saved card)
  const showSelectedState = isSelected && !comingSoon && !isUnavailable && !showAddCardAction;

  return (
    <View
      style={[
        styles.container,
        showSelectedState && styles.containerSelected,
        isDisabled && styles.containerDisabled,
      ]}>
      <Pressable
        style={styles.row}
        onPress={onSelect}
        disabled={isDisabled}
        testID={showAddCardAction ? 'payment-method-card-add-pressable' : undefined}
        accessibilityRole="radio"
        accessibilityState={{checked: isSelected, disabled: isDisabled}}>
        <RadioButton
          selected={isSelected && !comingSoon && !isUnavailable ? true : false}
          disabled={isDisabled}
        />
        {showAddCardAction ? (
          <View style={styles.cardAddContent}>
            <View testID="payment-method-card-add-top-row" style={styles.cardAddTopRow}>
              <View style={[styles.labelContainer, isWebDesktop && styles.cardAddDesktopLabelRow]}>
                <Text style={[styles.label, isDisabled && styles.labelDisabled]} numberOfLines={1}>
                  {label}
                </Text>
                {isWebDesktop ? <View style={styles.cardAddInlineIcons}>{icon}</View> : null}
              </View>
              <View style={styles.addActionInline}>
                <Text style={styles.actionText}>Add Card</Text>
                <Text style={styles.actionArrow}> →</Text>
              </View>
            </View>
            {!isWebDesktop ? (
              <View testID="payment-method-card-supported-icons-row" style={styles.cardAddIconsRow}>
                {icon}
              </View>
            ) : null}
          </View>
        ) : (
          <>
            <View style={showInlineCardDetails ? styles.cardInfoContainer : styles.labelContainer}>
              <Text style={[styles.label, isDisabled && styles.labelDisabled]} numberOfLines={1}>
                {label}
              </Text>
              {showInlineCardDetails ? (
                <Text style={styles.cardExpiry}>
                  Expiry {formatExpiry(savedCard.expiryMonth, savedCard.expiryYear)}
                </Text>
              ) : null}
            </View>
            {comingSoon ? <ComingSoonBadge /> : null}
            {isUnavailable && !comingSoon ? <UnavailableBadge /> : null}
            <View style={styles.iconWrapper}>{icon}</View>
          </>
        )}
      </Pressable>

      {/* Inline card actions for saved card */}
      {showInlineCardDetails && (onChangeCard || onDeleteCard) ? (
        <View style={styles.cardActionsContainer}>
          {isCardActionLoading ? (
            <ActivityIndicator size="small" color={theme.colour.uiControls?.primary ?? '#0054A6'} />
          ) : (
            <>
              {onChangeCard ? (
                <Pressable onPress={onChangeCard} style={styles.actionButton}>
                  <Text style={styles.actionText}>CHANGE CARD</Text>
                  <Text style={styles.actionArrow}> →</Text>
                </Pressable>
              ) : null}
              {onDeleteCard ? (
                <Pressable onPress={onDeleteCard} style={styles.actionButton}>
                  <Text style={[styles.actionText, styles.deleteText]}>DELETE</Text>
                  <Text style={[styles.actionArrow, styles.deleteText]}> →</Text>
                </Pressable>
              ) : null}
            </>
          )}
        </View>
      ) : null}

      {/* Show unavailable reason message */}
      {isUnavailable && !comingSoon ? (
        <View style={styles.unavailableReasonContainer}>
          <Text style={styles.unavailableReasonText}>{unavailableReason}</Text>
        </View>
      ) : null}

      {/* Legacy children support for non-card types */}
      {children && !showInlineCardDetails && !comingSoon && !isUnavailable ? (
        <View style={styles.expandedContent}>{children}</View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderWidth: 1,
    borderColor: '#E0E0E0',
    borderRadius: 8,
    backgroundColor: '#FFF',
    overflow: 'hidden',
  },
  containerSelected: {
    borderColor: theme.colour.uiControls?.primary ?? '#0054A6',
    borderWidth: 2,
  },
  containerDisabled: {
    opacity: 0.5,
    backgroundColor: '#F5F5F5',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 16,
    paddingHorizontal: 16,
  },
  radio: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: theme.colour.uiControls?.primary ?? '#0054A6',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  radioDisabled: {
    borderColor: '#CCC',
  },
  radioInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: theme.colour.uiControls?.primary ?? '#0054A6',
  },
  radioInnerDisabled: {
    backgroundColor: '#CCC',
  },
  labelContainer: {
    flex: 1,
  },
  cardInfoContainer: {
    flex: 1,
  },
  cardAddContent: {
    flex: 1,
    minWidth: 0,
  },
  cardAddTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  cardAddDesktopLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  cardAddInlineIcons: {
    marginLeft: 8,
  },
  cardAddIconsRow: {
    marginTop: 8,
    alignItems: 'flex-start',
  },
  label: {
    fontSize: 16,
    fontWeight: '500',
    color: '#333',
  },
  labelDisabled: {
    color: '#999',
  },
  cardExpiry: {
    fontSize: 14,
    color: '#666',
    marginTop: 4,
  },
  iconWrapper: {
    marginLeft: 12,
  },
  addActionInline: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 'auto',
    paddingLeft: 12,
  },
  cardActionsContainer: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingBottom: 16,
    paddingTop: 0,
    marginLeft: 32, // Align with text after radio button
    gap: 24,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  actionText: {
    fontSize: 14,
    fontWeight: '500',
    color: theme.colour.uiControls?.primary ?? '#0054A6',
  },
  actionArrow: {
    fontSize: 14,
    color: theme.colour.uiControls?.primary ?? '#0054A6',
  },
  deleteText: {
    color: '#C81E1E',
  },
  expandedContent: {
    paddingHorizontal: 16,
    paddingBottom: 16,
    paddingTop: 0,
  },
  comingSoonBadge: {
    backgroundColor: '#F0F0F0',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    marginRight: 8,
  },
  comingSoonText: {
    fontSize: 11,
    fontWeight: '500',
    color: '#666',
  },
  unavailableBadge: {
    backgroundColor: '#FEE2E2',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    marginRight: 8,
  },
  unavailableBadgeText: {
    fontSize: 11,
    fontWeight: '500',
    color: '#DC2626',
  },
  unavailableReasonContainer: {
    paddingHorizontal: 16,
    paddingBottom: 12,
    paddingTop: 0,
  },
  unavailableReasonText: {
    fontSize: 12,
    color: '#6B7280',
    lineHeight: 16,
  },
});
